'use client';

import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
    motion,
    useInView,
    useMotionTemplate,
    useMotionValueEvent,
    useScroll,
    useTransform,
    type MotionValue,
} from 'framer-motion';
import type { IconType } from 'react-icons';
import { TbCheck, TbEye, TbTargetArrow } from 'react-icons/tb';
import GlareHover from '@/app/components/GlareHover';
import type { CompanyProfileContent } from '@/app/lib/profile-content';

type MissionVisionItem = CompanyProfileContent['missionVision'][number];
type MissionVisionVideos = {
    missionDesktop: string;
    missionMobile: string;
    visionDesktop: string;
    visionMobile: string;
};
type MissionVisionRevealProps = {
    items: MissionVisionItem[];
    pointIcons: ReactNode[];
    isAr: boolean;
    videos: MissionVisionVideos;
};

const PANEL_BASE = '#03162f';

interface Reveal {
    opacity: MotionValue<number>;
    y: MotionValue<number>;
    filter: MotionValue<string>;
}

/** A single element's cinematic entrance: blurred/lowered/transparent at
 *  `start`, sharp/settled/opaque by `end`, then holds steady while the video
 *  keeps scrubbing underneath — so text arrives gradually instead of popping in. */
function useReveal(scrollYProgress: MotionValue<number>, start: number, end: number): Reveal {
    const opacity = useTransform(scrollYProgress, [start, end], [0, 1]);
    const y = useTransform(scrollYProgress, [start, end], [22, 0]);
    const blur = useTransform(scrollYProgress, [start, end], [10, 0]);
    const filter = useMotionTemplate`blur(${blur}px)`;
    return { opacity, y, filter };
}

interface PanelReveals {
    iconKicker: Reveal;
    headline: Reveal;
    body: Reveal;
    points: [Reveal, Reveal, Reveal];
}

/** Staggers icon/kicker -> headline -> body -> each point row across a fixed
 *  cascade of scroll-progress offsets from `start`, so the whole text block
 *  reveals as one continuous sequence tied to the same scrollYProgress driving
 *  the video scrub, rather than a single fade triggered once at the top. */
function usePanelReveals(scrollYProgress: MotionValue<number>, start: number): PanelReveals {
    return {
        iconKicker: useReveal(scrollYProgress, start, start + 0.05),
        headline: useReveal(scrollYProgress, start + 0.03, start + 0.1),
        body: useReveal(scrollYProgress, start + 0.07, start + 0.14),
        points: [
            useReveal(scrollYProgress, start + 0.11, start + 0.17),
            useReveal(scrollYProgress, start + 0.15, start + 0.21),
            useReveal(scrollYProgress, start + 0.19, start + 0.25),
        ],
    };
}

interface VideoBackdropProps {
    videoRef: RefObject<HTMLVideoElement | null>;
    videoSrc?: string;
    poster: string;
    imageY: MotionValue<number>;
    variant: 'desktop' | 'mobile';
}

/**
 * Withholds the video source until the element is near the viewport.
 *
 * These panels sit well below the fold, but an autoplaying <video> downloads in
 * full as soon as it is parsed — `preload="metadata"` does not restrain autoplay.
 * So they were pulling their clips (and 200 KB posters) during initial load, in
 * competition with the hero's LCP image. Trello P-01.
 *
 * Loading starts 300px before the panel scrolls into view, so by the time it is on
 * screen the clip is already playing — visually unchanged, just not on the
 * critical path.
 */
function useNearViewport(ref: RefObject<Element | null>) {
    const [near, setNear] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || near) return;

        // Without IntersectionObserver, load immediately rather than never.
        if (typeof IntersectionObserver === 'undefined') {
            setNear(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setNear(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '300px' },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [ref, near]);

    return near;
}

function VideoBackdrop({ videoRef, videoSrc, poster, imageY, variant }: VideoBackdropProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const near = useNearViewport(containerRef);

    return (
        <>
            <motion.div ref={containerRef} style={{ y: imageY }} className="absolute inset-x-0 -top-10 -bottom-10">
                {near && videoSrc ? (
                    <video
                        ref={videoRef}
                        src={videoSrc}
                        poster={poster}
                        aria-hidden="true"
                        muted
                        autoPlay
                        loop
                        playsInline
                        preload="none"
                        className="h-full w-full object-cover"
                    />
                ) : null}
            </motion.div>

            {variant === 'desktop' ? (
                <>
                    <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(3,10,25,0.82)_0%,rgba(3,10,25,0.5)_34%,rgba(3,10,25,0.08)_58%,transparent_72%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,10,25,0.25)_0%,transparent_22%,transparent_78%,rgba(3,10,25,0.55)_100%)]" />
                </>
            ) : (
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,10,25,0.15)_0%,rgba(3,10,25,0.35)_45%,rgba(3,10,25,0.96)_100%)]" />
            )}

            <GlareHover
                width="100%"
                height="100%"
                background="transparent"
                borderColor="transparent"
                borderRadius="0px"
                glareColor="#ffb53e"
                glareOpacity={0.18}
                glareAngle={-35}
                glareSize={220}
                transitionDuration={900}
                className="!border-0"
                style={{ position: 'absolute', inset: 0 }}
            />
        </>
    );
}

function ProgressPillRow({
    items,
    active,
    scrollYProgress,
    size = 'desktop',
}: {
    items: MissionVisionItem[];
    active: number;
    scrollYProgress: MotionValue<number>;
    size?: 'desktop' | 'mobile';
}) {
    return (
        <>
            <div className="pointer-events-none absolute inset-x-0 top-0 z-[60] h-[3px] bg-white/10">
                <motion.div style={{ scaleX: scrollYProgress }} className="h-full origin-left bg-gradient-to-r from-brand-text to-brand-purple" />
            </div>

            <div
                className={`pointer-events-none absolute inset-x-0 z-[60] flex items-center justify-center gap-2 px-4 ${
                    size === 'desktop' ? 'top-24 gap-3 px-5' : 'top-20'
                }`}
            >
                {items.map((item, index) => (
                    <div
                        key={item.title}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-heading text-[10px] font-black uppercase tracking-wide backdrop-blur-md transition-colors duration-500 ${
                            size === 'desktop' ? 'px-4 py-2 text-xs md:text-sm' : ''
                        } ${
                            active === index
                                ? 'border-brand-text/60 bg-brand-text/15 text-brand-text'
                                : 'border-white/15 bg-white/5 text-white/45'
                        }`}
                    >
                        <span>0{index + 1}</span>
                        <span className="h-1 w-1 rounded-full bg-current" />
                        <span>{item.title}</span>
                    </div>
                ))}
            </div>
        </>
    );
}

interface DesktopPanelProps {
    item: MissionVisionItem;
    Icon: IconType;
    pointIcons: ReactNode[];
    videoRef: RefObject<HTMLVideoElement | null>;
    videoSrc?: string;
    imageY: MotionValue<number>;
    reveals: PanelReveals;
    isAr: boolean;
    /** Mission and Vision get their own accent (red / purple) instead of
     *  sharing one red icon chip, so red isn't the only color in the reveal. */
    accentColor?: string;
}

function DesktopPanel({ item, Icon, pointIcons, videoRef, videoSrc, imageY, reveals, isAr, accentColor = '#ea3228' }: DesktopPanelProps) {
    return (
        <div className="relative h-full w-full overflow-hidden" style={{ backgroundColor: PANEL_BASE }}>
            <VideoBackdrop videoRef={videoRef} videoSrc={videoSrc} poster={item.image} imageY={imageY} variant="desktop" />

            {/* Pinned to the true physical left (not logical `start`): the video's empty
                zone is baked into the source composition, so it must not flip under RTL. */}
            <div className="absolute inset-y-0 left-0 z-10 flex w-full max-w-xl items-center px-6 sm:px-10 md:px-16 lg:px-20" dir="ltr">
                <div className="w-full" dir={isAr ? 'rtl' : 'ltr'}>
                    <motion.div style={reveals.iconKicker} className="mb-6 flex items-center gap-3">
                        <span
                            className="flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-[0_16px_34px_rgba(8,24,33,0.35)]"
                            style={{ backgroundColor: accentColor }}
                        >
                            <Icon className="h-6 w-6" aria-hidden="true" />
                        </span>
                        <span className="font-heading text-sm font-black uppercase tracking-[0.2em] text-brand-text">{item.title}</span>
                    </motion.div>
                    <motion.h3
                        style={reveals.headline}
                        className="no-en-title-scale font-heading text-3xl font-black leading-tight text-white md:text-5xl"
                    >
                        {item.kicker}
                    </motion.h3>
                    <motion.p style={reveals.body} className="mt-5 text-base leading-relaxed text-white/75 md:text-lg">
                        {item.body}
                    </motion.p>
                    <div className="mt-8 grid gap-3">
                        {item.points.map((point, i) => (
                            <motion.div
                                key={point}
                                style={reveals.points[i]}
                                className="flex items-center gap-3 border-t border-white/15 pt-3 text-sm font-semibold text-white/90 md:text-base"
                            >
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-brand-text/30 bg-brand-text/10 text-brand-text backdrop-blur-sm">
                                    {pointIcons[i] ?? <TbCheck className="h-4 w-4" aria-hidden="true" />}
                                </span>
                                <span>{point}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

interface MobilePanelProps {
    item: MissionVisionItem;
    Icon: IconType;
    pointIcons: ReactNode[];
    videoRef: RefObject<HTMLVideoElement | null>;
    videoSrc?: string;
    imageY: MotionValue<number>;
    reveals: PanelReveals;
    accentColor?: string;
}

function MobilePanel({ item, Icon, pointIcons, videoRef, videoSrc, imageY, reveals, accentColor = '#ea3228' }: MobilePanelProps) {
    return (
        <div className="relative h-full w-full overflow-hidden" style={{ backgroundColor: PANEL_BASE }}>
            <VideoBackdrop videoRef={videoRef} videoSrc={videoSrc} poster={item.image} imageY={imageY} variant="mobile" />

            <div className="absolute inset-x-0 bottom-0 z-10 p-6 pb-10">
                <motion.div style={reveals.iconKicker} className="mb-4 flex items-center gap-3">
                    <span
                        className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-[0_14px_28px_rgba(8,24,33,0.35)]"
                        style={{ backgroundColor: accentColor }}
                    >
                        <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="font-heading text-xs font-black uppercase tracking-[0.2em] text-brand-text">{item.title}</span>
                </motion.div>
                {/* Not a native heading: DesktopPanel's lg:block sibling renders
                    this exact text as the real <h3>. This container is only
                    lg:hidden via CSS, so both panels exist in the DOM at once —
                    a second <h3> here would duplicate the heading regardless of
                    which panel is visually shown. */}
                <motion.p
                    style={reveals.headline}
                    className="no-en-title-scale font-heading text-2xl font-black leading-tight text-white"
                    role="heading"
                    aria-level={3}
                >
                    {item.kicker}
                </motion.p>
                <motion.p style={reveals.body} className="mt-3 text-sm leading-relaxed text-white/80">
                    {item.body}
                </motion.p>
                <div className="mt-5 grid gap-2.5">
                    {item.points.map((point, i) => (
                        <motion.div
                            key={point}
                            style={reveals.points[i]}
                            className="flex items-center gap-3 border-t border-white/15 pt-2.5 text-xs font-semibold text-white/90"
                        >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-brand-text/30 bg-brand-text/10 text-brand-text">
                                {pointIcons[i] ?? <TbCheck className="h-3.5 w-3.5" aria-hidden="true" />}
                            </span>
                            <span>{point}</span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// The page wraps every section in an `overflow-hidden` ancestor for decorative
// clipping elsewhere, which breaks native `position: sticky` (it needs a real
// scrolling ancestor). Drive the pin manually instead: fixed while in range,
// absolute-to-top before it and absolute-to-bottom after, so the panel still
// reads as pinned-then-released relative to the viewport.
function usePinPhase(scrollYProgress: MotionValue<number>) {
    const [phase, setPhase] = useState<'before' | 'pinned' | 'after'>('before');
    useMotionValueEvent(scrollYProgress, 'change', (v) => {
        setPhase(v <= 0 ? 'before' : v >= 1 ? 'after' : 'pinned');
    });
    return phase;
}

// Scroll-progress thresholds for desktop, tuned so both clips scrub at a
// consistent, deliberate pace: Mission is ~10s, Vision-desktop is ~8s, and the
// 450vh section gives ~180-190px of scroll per second of footage for each
// (vs. a flat 0.6/0.36 split, which ran Vision ~30% faster than Mission).
const D_MISSION_END = 0.58;
const D_VISION_START = 0.52;
const D_CURTAIN_END = 0.6;
const D_VISION_CONTENT_START = 0.56;

function DesktopPinnedReveal({ items, pointIcons, isAr, videos }: MissionVisionRevealProps) {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(0);
    const [visionUnlocked, setVisionUnlocked] = useState(false);
    const mission = items[0];
    const vision = items[1];

    const missionVideoRef = useRef<HTMLVideoElement>(null);
    const visionVideoRef = useRef<HTMLVideoElement>(null);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end end'],
    });
    const phase = usePinPhase(scrollYProgress);
    const missionInView = useInView(sectionRef, { margin: '200px 0px 200px 0px' });

    useMotionValueEvent(scrollYProgress, 'change', (v) => {
        setActive(v > (D_VISION_START + D_CURTAIN_END) / 2 ? 1 : 0);
        if (v > 0.1) setVisionUnlocked(true);
    });

    const missionReveals = usePanelReveals(scrollYProgress, 0);
    const visionReveals = usePanelReveals(scrollYProgress, D_VISION_CONTENT_START);

    const missionScale = useTransform(scrollYProgress, [D_VISION_START, D_MISSION_END], [1, 0.94]);
    const missionDarken = useTransform(scrollYProgress, [D_VISION_START, D_MISSION_END], [0, 0.55]);
    const missionImageY = useTransform(scrollYProgress, [0, D_MISSION_END], [0, -40]);

    const visionY = useTransform(scrollYProgress, [D_VISION_START, D_CURTAIN_END], ['100%', '0%']);
    const visionImageY = useTransform(scrollYProgress, [D_VISION_START, 1], [30, -20]);

    return (
        <section
            ref={sectionRef}
            data-nav-hide="cinematic"
            className="relative hidden h-[450vh] lg:block"
            style={{ backgroundColor: PANEL_BASE }}
        >
            {/* Anchored to the section's own top (not the phase-controlled fixed wrapper
                below), so it scrolls away with the page as soon as the pin engages instead
                of staying stuck next to the navbar for the whole pinned scroll. */}
            <ProgressPillRow items={items} active={active} scrollYProgress={scrollYProgress} size="desktop" />

            <div
                className={`h-[100svh] w-full overflow-hidden ${
                    phase === 'pinned'
                        ? 'fixed inset-x-0 top-0'
                        : phase === 'after'
                          ? 'absolute inset-x-0 bottom-0'
                          : 'absolute inset-x-0 top-0'
                }`}
            >
                <motion.div style={{ scale: missionScale }} className="absolute inset-0 z-10 origin-center">
                    <DesktopPanel
                        item={mission}
                        Icon={TbTargetArrow}
                        pointIcons={pointIcons.slice(0, 3)}
                        videoRef={missionVideoRef}
                        videoSrc={missionInView ? videos.missionDesktop : undefined}
                        imageY={missionImageY}
                        reveals={missionReveals}
                        isAr={isAr}
                    />
                    <motion.div style={{ opacity: missionDarken }} className="pointer-events-none absolute inset-0 z-20 bg-black" />
                </motion.div>

                <motion.div
                    style={{ y: visionY }}
                    className="absolute inset-0 z-30 overflow-hidden rounded-t-[3rem] shadow-[0_-40px_100px_rgba(0,0,0,0.45)]"
                >
                    <DesktopPanel
                        item={vision}
                        Icon={TbEye}
                        pointIcons={pointIcons.slice(3, 6)}
                        videoRef={visionVideoRef}
                        videoSrc={visionUnlocked ? videos.visionDesktop : undefined}
                        imageY={visionImageY}
                        reveals={visionReveals}
                        isAr={isAr}
                        accentColor="#7a2ff2"
                    />
                </motion.div>
            </div>
        </section>
    );
}

// Mobile's Vision clip runs ~10s (vs. desktop's ~8s), so it gets its own,
// slightly more even split than desktop rather than reusing the same numbers.
const M_MISSION_END = 0.53;
const M_VISION_START = 0.47;
const M_CURTAIN_END = 0.55;
const M_VISION_CONTENT_START = 0.51;

function MobilePinnedReveal({ items, pointIcons, videos }: MissionVisionRevealProps) {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(0);
    const [visionUnlocked, setVisionUnlocked] = useState(false);
    const mission = items[0];
    const vision = items[1];

    const missionVideoRef = useRef<HTMLVideoElement>(null);
    const visionVideoRef = useRef<HTMLVideoElement>(null);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end end'],
    });
    const phase = usePinPhase(scrollYProgress);
    const missionInView = useInView(sectionRef, { margin: '200px 0px 200px 0px' });

    useMotionValueEvent(scrollYProgress, 'change', (v) => {
        setActive(v > (M_VISION_START + M_CURTAIN_END) / 2 ? 1 : 0);
        if (v > 0.1) setVisionUnlocked(true);
    });

    const missionReveals = usePanelReveals(scrollYProgress, 0);
    const visionReveals = usePanelReveals(scrollYProgress, M_VISION_CONTENT_START);

    const missionScale = useTransform(scrollYProgress, [M_VISION_START, M_MISSION_END], [1, 0.94]);
    const missionDarken = useTransform(scrollYProgress, [M_VISION_START, M_MISSION_END], [0, 0.55]);
    const missionImageY = useTransform(scrollYProgress, [0, M_MISSION_END], [0, -30]);

    const visionY = useTransform(scrollYProgress, [M_VISION_START, M_CURTAIN_END], ['100%', '0%']);
    const visionImageY = useTransform(scrollYProgress, [M_VISION_START, 1], [20, -15]);

    return (
        <section
            ref={sectionRef}
            data-nav-hide="cinematic"
            className="relative h-[450vh] lg:hidden"
            style={{ backgroundColor: PANEL_BASE }}
        >
            <ProgressPillRow items={items} active={active} scrollYProgress={scrollYProgress} size="mobile" />

            <div
                className={`h-[100svh] w-full overflow-hidden ${
                    phase === 'pinned'
                        ? 'fixed inset-x-0 top-0'
                        : phase === 'after'
                          ? 'absolute inset-x-0 bottom-0'
                          : 'absolute inset-x-0 top-0'
                }`}
            >
                <motion.div style={{ scale: missionScale }} className="absolute inset-0 z-10 origin-center">
                    <MobilePanel
                        item={mission}
                        Icon={TbTargetArrow}
                        pointIcons={pointIcons.slice(0, 3)}
                        videoRef={missionVideoRef}
                        videoSrc={missionInView ? videos.missionMobile : undefined}
                        imageY={missionImageY}
                        reveals={missionReveals}
                    />
                    <motion.div style={{ opacity: missionDarken }} className="pointer-events-none absolute inset-0 z-20 bg-black" />
                </motion.div>

                <motion.div
                    style={{ y: visionY }}
                    className="absolute inset-0 z-30 overflow-hidden rounded-t-[2.5rem] shadow-[0_-40px_100px_rgba(0,0,0,0.45)]"
                >
                    <MobilePanel
                        item={vision}
                        Icon={TbEye}
                        pointIcons={pointIcons.slice(3, 6)}
                        videoRef={visionVideoRef}
                        videoSrc={visionUnlocked ? videos.visionMobile : undefined}
                        imageY={visionImageY}
                        reveals={visionReveals}
                        accentColor="#7a2ff2"
                    />
                </motion.div>
            </div>
        </section>
    );
}

function SeamBridge({ from, to }: { from: string; to: string }) {
    return (
        <div
            aria-hidden="true"
            className="relative h-14 overflow-hidden md:h-24"
            style={{ background: `linear-gradient(180deg, ${from} 0%, ${to} 100%)` }}
        >
            <div
                className="absolute inset-x-0 top-1/2 h-16 -translate-y-1/2 opacity-40 md:h-24"
                style={{ background: 'radial-gradient(closest-side, rgba(255,181,62,0.35), transparent 70%)' }}
            />
        </div>
    );
}

export default function MissionVisionReveal({ items, pointIcons, isAr, videos }: MissionVisionRevealProps) {
    return (
        <>
            <SeamBridge from="#ffb53e" to={PANEL_BASE} />
            <DesktopPinnedReveal items={items} pointIcons={pointIcons} isAr={isAr} videos={videos} />
            <MobilePinnedReveal items={items} pointIcons={pointIcons} isAr={isAr} videos={videos} />
            <SeamBridge from={PANEL_BASE} to="#ffffff" />
        </>
    );
}
